import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { splitItem } from "../../vendor/src/features/remotion-editor/editor/state/actions/split-item";
import { editableTree } from "./group-split";
import { getPositionOffsetAt } from "../../vendor/src/features/remotion-editor/editor/items/animation/position-keyframes";

/** Open a time slot without replaying media; crossing static graphics cover the new slot. */
export function insertTimelineTime(
  state: EditorState,
  trackIds: readonly string[],
  at: number,
  duration: number,
  excludeIds: readonly string[] = [],
  options: { crossingVisuals?: "hold" | "split" } = {},
): { ok: boolean; state: EditorState } {
  if (
    !Number.isSafeInteger(at) ||
    at < 0 ||
    !Number.isSafeInteger(duration) ||
    duration < 0 ||
    !Number.isFinite(state.undoableState.fps) ||
    state.undoableState.fps <= 0
  )
    return { ok: false, state };
  if (!duration) return { ok: true, state };
  if (
    trackIds.some(
      (id) => !state.undoableState.tracks.some((track) => track.id === id),
    )
  )
    return { ok: false, state };
  if (
    !state.undoableState.tracks.some(
      (track) => trackIds.includes(track.id) && !track.locked,
    )
  )
    return { ok: true, state };
  const targets = new Set(trackIds),
    excluded = new Set(excludeIds);
  let next = state;
  const isStatic = (type: string) =>
    ["image", "solid", "text", "blur", "pdf", "ppt"].includes(type);
  const holdStatic = (
    item: EditorState["undoableState"]["items"][string],
    track: EditorState["undoableState"]["tracks"][number],
  ) =>
    isStatic(item.type) &&
    (options.crossingVisuals !== "split" ||
      (item.timelineSourceItemId ?? item.id).startsWith("waka-story-root-") ||
      track.metadata?.trackType === "brand-template");
  for (const track of state.undoableState.tracks) {
    if (!targets.has(track.id) || track.locked) continue;
    for (const id of track.items) {
      const item = state.undoableState.items[id];
      if (!item) return { ok: false, state };
      if (
        !excluded.has(id) &&
        item.from + item.durationInFrames > at &&
        !editableTree(state, id)
      )
        return { ok: false, state };
    }
  }
  for (const track of state.undoableState.tracks) {
    if (!targets.has(track.id) || track.locked) continue;
    for (const id of track.items) {
      const item = next.undoableState.items[id]!;
      if (
        excluded.has(id) ||
        holdStatic(item, track) ||
        item.from >= at ||
        item.from + item.durationInFrames <= at
      )
        continue;
      const divided = splitItem({
        state: next,
        idToSplit: id,
        framePosition: at,
      });
      if (divided === next) return { ok: false, state };
      next = divided;
    }
  }
  const items = { ...next.undoableState.items };
  const shifted = new Set<string>();
  const shift = (id: string) => {
    if (shifted.has(id) || excluded.has(id)) return;
    shifted.add(id);
    const item = items[id];
    if (!item) return;
    if (item.type === "group") item.children.forEach(shift);
    items[id] = { ...item, from: item.from + duration };
  };
  for (const track of next.undoableState.tracks) {
    if (!targets.has(track.id) || track.locked) continue;
    for (const id of track.items) {
      const item = items[id]!;
      if (excluded.has(id)) continue;
      const persistent =
        (item.timelineSourceItemId ?? item.id).startsWith("waka-story-root-") ||
        track.metadata?.trackType === "brand-template";
      if (
        holdStatic(item, track) &&
        item.from + item.durationInFrames > at &&
        (item.from < at || (persistent && item.from === at))
      ) {
        const local = at - item.from,
          nextItem = {
            ...item,
            durationInFrames: item.durationInFrames + duration,
          };
        if (
          "positionKeyframes" in item &&
          "positionKeyframes" in nextItem &&
          item.positionKeyframes?.length
        ) {
          const before = getPositionOffsetAt(
              item.positionKeyframes,
              Math.max(0, local - 1),
            ),
            after = getPositionOffsetAt(item.positionKeyframes, local);
          const points = item.positionKeyframes.map((point) => ({
            ...point,
            frame: point.frame >= local ? point.frame + duration : point.frame,
          }));
          points.push(
            { frame: Math.max(0, local - 1), ...before },
            { frame: local + duration - 1, ...before },
            { frame: local + duration, ...after },
          );
          nextItem.positionKeyframes = [
            ...new Map(points.map((point) => [point.frame, point])).values(),
          ].sort((a, b) => a.frame - b.frame);
        }
        items[id] = nextItem;
      } else if (item.from >= at) shift(id);
    }
  }
  const transitions = Object.fromEntries(
    Object.entries(next.undoableState.transitions).map(([id, t]) => [
      id,
      targets.has(t.trackId) &&
      !next.undoableState.tracks.find((track) => track.id === t.trackId)
        ?.locked &&
      t.from >= at
        ? { ...t, from: t.from + duration }
        : t,
    ]),
  );
  return {
    ok: true,
    state: {
      ...next,
      selectedItems: state.selectedItems.filter((id) => items[id]),
      timelineTimeEditApplied: true,
      undoableState: { ...next.undoableState, items, transitions },
    },
  };
}
